import React, { useState, useEffect, useRef } from 'react';
import { X, AlertCircle, CheckCircle2, LoaderCircle } from 'lucide-react';
import { 
  checkOutlookConnection, 
  disconnectOutlook
} from '../../service/apiService';
import MicrosoftOutlookIcon from '../../icons/microsoftOutlook';

// No global declarations needed - using direct OAuth URLs

interface OutlookAuthProps {
  isOpen: boolean;
  onClose: () => void;
  onAuthSuccess: (code: string) => void;
}

const OutlookAuth: React.FC<OutlookAuthProps> = ({
  isOpen,
  onClose,
  onAuthSuccess,
}) => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSuccess, setAuthSuccess] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [checkingConnection, setCheckingConnection] = useState(false);
  const [lastAction, setLastAction] = useState<'connect' | 'disconnect' | null>(null);
  const authInstanceRef = useRef<any>(null);

  // Outlook OAuth configuration
  const OUTLOOK_CLIENT_ID = process.env.REACT_APP_OUTLOOK_CLIENT_ID || ""
  // `offline_access` is REQUIRED for Microsoft to return a refresh_token — without it
  // scheduled/background jobs (VA + Outlook monitor) can't refresh the expired access token.
  // Must stay in sync with OutlookService.scopes (api) — the token exchange asks for these
  // same scopes, and requesting one that wasn't authorized here fails with AADSTS65001.
  // `User.Read` backs the /me call that resolves the connected mailbox's address.
  const OUTLOOK_SCOPE = 'offline_access https://graph.microsoft.com/Mail.Read https://graph.microsoft.com/Mail.Send https://graph.microsoft.com/Mail.ReadWrite https://graph.microsoft.com/User.Read';

  useEffect(() => {
    if (!isOpen) {
      setAuthError(null);
      setAuthSuccess(false);
      setIsConnecting(false);
      setIsDisconnecting(false);
      setLastAction(null);
    } else {
      checkConnectionStatus();
    }
  }, [isOpen]);

  const checkConnectionStatus = async () => {
    console.log('Checking Outlook connection status...');
    setCheckingConnection(true);
    try {
      const connected = await checkOutlookConnection();
      console.log('Outlook connection status:', connected);
      setIsConnected(connected);
    } catch (error) {
      console.error('Error checking Outlook connection:', error);
    } finally {
      setCheckingConnection(false);
    }
  };

  useEffect(() => {
    // Initialize OAuth client similar to Google's initCodeClient
    if (!authInstanceRef.current) {
      authInstanceRef.current = {
        requestCode: () => {
          // Create a unique state parameter for security
          const state = Math.random().toString(36).substring(2, 15);
          const authUrl = buildAuthUrl(state);
          
          const popup = window.open(
            authUrl,
            'outlookAuth',
            'width=500,height=600,scrollbars=yes,resizable=yes'
          );
          
          if (!popup) {
            setIsConnecting(false);
            setAuthError('Popup blocked. Please allow popups for this site.');
            return;
          }

          // Poll the popup URL to detect when OAuth redirect happens
          const pollTimer = setInterval(() => {
            try {
              if (popup.closed) {
                clearInterval(pollTimer);
                setIsConnecting(false);
                setAuthError('Authentication was cancelled');
                return;
              }

              // Check if popup URL has changed to our domain (after OAuth redirect)
              const popupUrl = popup.location.href;
              if (popupUrl.includes(window.location.origin)) {
                const urlParams = new URLSearchParams(popup.location.search);
                const code = urlParams.get('code');
                const error = urlParams.get('error');
                const returnedState = urlParams.get('state');

                if (error) {
                  clearInterval(pollTimer);
                  popup.close();
                  setIsConnecting(false);
                  setAuthError(urlParams.get('error_description') || error);
                  return;
                }

                if (code && returnedState === state) {
                  console.log('Outlook authorization code:', code);
                  
                  clearInterval(pollTimer);
                  popup.close();
                  
                  setIsConnecting(false);
                  setAuthSuccess(true);
                  setLastAction('connect');
                  onAuthSuccess(code);
                  
                  setTimeout(() => {
                    checkConnectionStatus();
                    setAuthSuccess(false);
                  }, 1500);
                }
              }
            } catch (e) {
              // Cross-origin error - popup is still on Microsoft's domain, continue polling
            }
          }, 1000);
        }
      };
    }
  }, [OUTLOOK_CLIENT_ID, onAuthSuccess]);

  const buildAuthUrl = (state: string) => {
    const params = new URLSearchParams({
      client_id: OUTLOOK_CLIENT_ID,
      response_type: 'code',
      redirect_uri: window.location.origin, // Redirect to main app, not separate callback
      scope: OUTLOOK_SCOPE,
      response_mode: 'query',
      // `select_account`, NOT `consent`. Forcing consent re-runs the grant every
      // time, and in a tenant that restricts user consent that re-triggers the
      // "Need admin approval" wall even where a grant already exists. Microsoft
      // returns the refresh_token from `offline_access`, so nothing is lost.
      prompt: 'select_account',
      state: state
    });
    
    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params.toString()}`;
  };

  const handleConnect = () => {
    console.log('Outlook connect button clicked ');
    setLastAction('connect');
    
    if (!OUTLOOK_CLIENT_ID) {
      setAuthError('Outlook Client ID not configured');
      return;
    }

    setIsConnecting(true);
    setAuthError(null);

    try {
      authInstanceRef.current?.requestCode();
    } catch (error) {
      setIsConnecting(false);
      setAuthError('Failed to initiate Outlook authentication');
      console.error('Outlook auth error:', error);
    }
  };

  const handleDisconnect = async () => {
    console.log('Outlook disconnect button clicked');
    setLastAction('disconnect');
    
    setIsDisconnecting(true);
    setAuthError(null);

    try {
      console.log('Calling disconnectOutlook API...');
      const result = await disconnectOutlook();
      console.log('API response:', result);
      
      if (result.success) {
        console.log('Disconnect successful!');
        setIsConnected(false);
        setAuthSuccess(true);
        
        setTimeout(() => {
          console.log('Closing modal after successful disconnect');
          onClose();
        }, 1500);
      } else {
        console.log('Disconnect failed with error:', result.error);
        setAuthError(result.error || 'Failed to disconnect Outlook account');
      }
    } catch (error) {
      console.error('Exception in disconnect:', error);
      setAuthError('Failed to disconnect Outlook account. Please try again.');
    } finally {
      console.log('Disconnect process completed');
      setIsDisconnecting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 scrim flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-base font-semibold text-gray-900 flex items-center">
            <MicrosoftOutlookIcon size={18} className="mr-2" />
            Outlook Connection
          </h3>
          <button
            onClick={() => {
              setIsConnecting(false);
              setIsDisconnecting(false);
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {checkingConnection ? (
          <div className="text-center py-8">
            <LoaderCircle className="w-8 h-8 text-violet-600 animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-600">Verifying connection...</p>
          </div>
        ) : authSuccess ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-3">
              <CheckCircle2 className="text-green-600" size={24} />
            </div>
            <h4 className="text-base font-semibold text-gray-900 mb-1">
              {lastAction === 'connect' ? 'Connected Successfully' : 'Disconnected Successfully'}
            </h4>
            <p className="text-sm text-gray-600">
              {lastAction === 'connect'
                ? 'Your Outlook account is now connected.' 
                : 'Your Outlook account has been removed.'
              }
            </p>
          </div>
        ) : (
          <>
            {isConnected && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 mb-4">
                <div className="flex items-center">
                  <CheckCircle2 className="text-green-600 mr-2 flex-shrink-0" size={16} />
                  <span className="text-sm text-green-800">Account connected</span>
                </div>
              </div>
            )}

            {authError && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="text-red-500 mr-2 flex-shrink-0 mt-0.5" size={16} />
                  <p className="text-sm text-red-700">{authError}</p>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleConnect}
                disabled={isConnecting || isDisconnecting}
                className="flex-1 bg-violet-600 text-white py-2.5 px-4 rounded-md text-sm font-medium hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isConnecting ? (
                  <>
                    <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
                    <span>Connecting...</span>
                  </>
                ) : (
                  'Connect Account'
                )}
              </button>
              
              <button
                onClick={handleDisconnect}
                disabled={isConnecting || isDisconnecting || !isConnected}
                className="flex-1 bg-white text-gray-700 py-2.5 px-4 rounded-md text-sm font-medium border border-gray-300 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isDisconnecting ? (
                  <>
                    <LoaderCircle className="w-4 h-4 animate-spin mr-2" />
                    <span>Disconnecting...</span>
                  </>
                ) : (
                  'Disconnect'
                )}
              </button>
            </div>

            <p className="text-xs text-gray-500 mt-4 text-center leading-relaxed">
              {isConnected 
                ? 'Disconnect to revoke Kyva\'s access to your Outlook account.'
                : 'Connect your Outlook account to enable email reading and sending features.'
              }
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default OutlookAuth;